import PropTypes from 'prop-types';
import {useCallback} from 'react';
import {Button, TextArea} from '@/components/atoms';
import {
  ImageUploadField,
  MultiLanguageFieldGroup
} from '@/components/molecules';
import {updateLanguageFieldMap} from '@/pages/PerfectStoreUtils';
import {
  rackHeaderUiSchema,
  rackImageHelperText,
  rackSubHeaderUiSchema
} from './schemas';

const RackDisplayCard = ({
  index,
  rack,
  languages,
  onChange,
  onRemove,
  showRemove
}) => {
  const handleImageChange = useCallback(
    (image) => onChange(index, {...rack, image}),
    [index, onChange, rack]
  );

  const handleImageDelete = useCallback(
    () => onChange(index, {...rack, image: null}),
    [index, onChange, rack]
  );

  const handleContentChange = useCallback(
    (langCode, field, value, hasError) => {
      onChange(index, {
        ...rack,
        content: updateLanguageFieldMap(
          rack.content,
          langCode,
          field,
          value,
          hasError
        )
      });
    },
    [index, onChange, rack]
  );

  return (
    <div className='mb-2' data-testid={`rack-display-card-${index}`}>
      {showRemove && (
        <div className='mb-2 flex justify-end'>
          <Button
            handleButton={() => onRemove(index)}
            uiSchema={{title: 'Remove', variant: 'secondary', size: 'small'}}
          />
        </div>
      )}
      <ImageUploadField
        label={`Image ${index + 1}*`}
        tooltipText={rackImageHelperText}
        required
        imageUri={rack.image?.imageUri}
        onChange={handleImageChange}
        onDelete={handleImageDelete}
        dataTestId={`rack-image-upload-${index}`}
      />
      <MultiLanguageFieldGroup
        className='mt-4'
        languages={languages}
        dataTestId={`rack-content-${index}`}
        renderField={(lang) => {
          const data = rack.content?.[lang.code] || {};
          return (
            <>
              <TextArea
                value={data.header || ''}
                label={rackHeaderUiSchema.label}
                uiSchema={rackHeaderUiSchema}
                updateValue={(val, hasError) =>
                  handleContentChange(lang.code, 'header', val, hasError)
                }
                trim={false}
              />
              <TextArea
                value={data.subheader || ''}
                label={rackSubHeaderUiSchema.label}
                uiSchema={rackSubHeaderUiSchema}
                updateValue={(val, hasError) =>
                  handleContentChange(lang.code, 'subheader', val, hasError)
                }
                trim={false}
              />
            </>
          );
        }}
      />
    </div>
  );
};

RackDisplayCard.propTypes = {
  index: PropTypes.number.isRequired,
  rack: PropTypes.shape({
    id: PropTypes.string,
    image: PropTypes.object,
    content: PropTypes.object
  }).isRequired,
  languages: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  showRemove: PropTypes.bool.isRequired
};

export default RackDisplayCard;
